import { useState, useEffect, useRef } from "react";
import Button from "@mui/material/Button";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router";
import { Outlet } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { CardActionArea } from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import LoadingButton from "@mui/lab/LoadingButton";
import EnrollmentService from "services/EnrollmentService.js";
import PopUpModal from "components/utils/PopUpModal.js";
import ConfirmActionModal from "components/modals/ConfirmActionModal";
import useAPI from "hooks/useAPI.js";
import DateUtils from "utils/DateUtils.js";
import { useOutletContext } from "react-router-dom";

export function Enrollments() {
  const [hospitalId] = useOutletContext();

  return <Outlet context={[hospitalId]} />;
}
export function EnrollmentList() {
  const [hospitalId] = useOutletContext();
  const [enrollmentList, setEnrollmentList] = useState([]);
  const { patientId } = useParams();

  const deleteEnrollmentModalRef = useRef(null);
  const [deletingEnrollmentId, setDeletingEnrollmentId] = useState("");

  const getEnrollmentsAPI = useAPI(
    EnrollmentService.getPatientEnrollments,
    getEnrollmentsHandler
  );
  const addEnrollmentAPI = useAPI(
    EnrollmentService.createEnrollment,
    addEnrollmentHandler
  );
  const deleteEnrollmentAPI = useAPI(
    EnrollmentService.deleteEnrollment,
    deleteEnrollmentHandler,
    true
  );

  function getEnrollments() {
    getEnrollmentsAPI.execute(hospitalId, patientId);
  }

  function addEnrollment() {
    addEnrollmentAPI.execute(hospitalId, patientId);
  }

  function deleteEnrollment(enrollmentId) {
    deleteEnrollmentAPI.execute(hospitalId, patientId, enrollmentId);
  }

  useEffect(() => {
    getEnrollments();
  }, []);

  /* API Success Handlers */

  function getEnrollmentsHandler(response) {
    let data = response.data;
    setEnrollmentList(data);
  }

  function addEnrollmentHandler(response) {
    setEnrollmentList((prevState) => [...prevState, response.data]);
  }

  function deleteEnrollmentHandler(response) {
    getEnrollments();
  }

  return (
    <Paper elevation={0} className="p-4">
      <Stack direction="row" justifyContent="right" alignItems="center">
        {addEnrollmentAPI.status === "pending" ? (
          <LoadingButton
            loading
            loadingPosition="start"
            startIcon={<img src="assets/plus-icon.svg" alt="Add icon" />}
            variant="contained"
          >
            Create Enrollment
          </LoadingButton>
        ) : (
          <Button
            className="text-right"
            onClick={addEnrollment}
            startIcon={<img src="assets/plus-icon.svg" alt="Add icon" />}
            variant="contained"
          >
            Create Enrollment
          </Button>
        )}
      </Stack>
      {getEnrollmentsAPI.status === "pending" ? (
        <div className="text-center">
          <CircularProgress
            className="text-center"
            sx={{
              my: 20,
            }}
          />
        </div>
      ) : (
        <Box
          sx={{
            mt: 3,
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(220px, 1fr))",
              lg: "repeat(3, minmax(220px, 1fr))",
            },
            gap: 2.25,
            alignItems: "stretch",
          }}
        >
          {enrollmentList.map((enrollment, index) => {
            return (
              <EnrollmentCard
                key={index}
                index={index + 1}
                enrollment={enrollment}
              />
            );
          })}
        </Box>
      )}
      <PopUpModal ref={deleteEnrollmentModalRef} modalWidth={400}>
        <ConfirmActionModal
          content="Do you want to confirm deleting the enrollment?"
          confirmActionHandler={() => {
            deleteEnrollment(deletingEnrollmentId);
            deleteEnrollmentModalRef.current.closeModal();
          }}
          onCloseHandler={() => deleteEnrollmentModalRef.current.closeModal()}
        />
      </PopUpModal>
    </Paper>
  );

  function EnrollmentCard({ enrollment, index }) {
    let navigate = useNavigate();

    return (
      <Card
        sx={{
          height: "100%",
          minHeight: 170,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.05)",
          transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: "0 8px 20px rgba(15, 23, 42, 0.08)",
            borderColor: "primary.light",
          },
        }}
      >
        <CardActionArea
          sx={{ height: "100%", alignItems: "stretch" }}
          onClick={() => navigate(`${enrollment.id}/sessions`)}
        >
          <CardContent
            sx={{
              p: 2.25,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{
                mb: 2,
              }}
            >
              <div>
                <Typography color="text.secondary" variant="body1">
                  Enrollment
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    mt: 0.75,
                    fontWeight: 700,
                  }}
                  color="text.primary"
                >
                  {index}
                </Typography>
              </div>
              <div>
                <Typography color="text.secondary" variant="body1">
                  Sessions
                </Typography>
                <Typography
                  sx={{
                    marginTop: 1,
                    textAlign: "center",
                  }}
                  component="div"
                >
                  <Chip
                    color="info"
                    label={enrollment.enrollmentSessions.length}
                  />
                </Typography>
              </div>
            </Stack>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-end"
              sx={{
                mt: "auto",
              }}
            >
              <div>
                <Typography color="text.secondary" variant="caption">
                  Created At
                </Typography>
                <Typography variant="body2" component="div" sx={{ fontWeight: 600 }}>
                  {DateUtils.formatDateTime(enrollment.createdAt)}
                </Typography>
              </div>
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  setDeletingEnrollmentId(enrollment.id);
                  deleteEnrollmentModalRef.current.openModal();
                }}
              >
                <Tooltip title="Delete Enrollment">
                  <DeleteOutlineIcon fontSize="inherit" color="error" />
                </Tooltip>
              </IconButton>
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
    );
  }
}
